import { Tooltip as BccTooltip, Box, Info, TooltipContent, TooltipTrigger } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { TFunction } from "i18next";
import moment from "moment/moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DatePicker } from "rsuite";

import { CalendarIcon } from "components/icons/CalendarIcon";
import { DeleteIcon } from "components/icons/DeleteIcon";
import Warning from "components/icons/Warning";
import {
    displayAmountErrorLabel,
    FEATURE_LICENSES,
    FEATURE_TENANT_MANAGEMENT,
    isExpirationDateValid,
    isSubscriptionLicense,
    LICENSE_REUSE_PRO_AMOUNT,
    LICENSE_REUSE_PRO_SCALE_AMOUNT,
    MOBILE_DIAGNOSTICS_ERASURE_LICENSE_REUSE_ID_AMOUNT,
    REUSE_LICENSE_IDS,
} from "components/licenses/common";
import style from "components/licenses/delivery-history/add-license-delivery.scss";
import {
    addMoreLicenses,
    createDefaultSelectedLicense,
    DeliveryForm,
    License,
} from "components/licenses/delivery-history/DeliveryFormContent";
import GlobalSet from "components/licenses/GlobalSet";
import SubscriptionExpiryWarningBanner from "components/licenses/subscription-expiry-warning-banner/SubscriptionExpiryWarningBanner";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import StaticTable from "components/support/api-guide/StaticTable";
import Tooltip from "components/tooltip/Tooltip";
import { DeliveryMode, MIN_DATE } from "domain/licenses";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isUserParentInternal } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import {
    DATE_FORMAT,
    formatDateWithoutTime,
    formatFormDate,
    formatFormDateWithoutTime,
    generateLastDate,
    HOUR,
    MINUTES_SECONDS_MILLISECONDS,
    parseDate,
    roundToOneDecimal,
} from "utils/format";

import testIds from "testIds.json";

interface Props {
    availableLicenses: License[];
    ownedLicenses: License[];
    setSelectedLicenses: (licenses: License[]) => void;
    selectedLicenses: License[];
    newDeal: boolean;
    deliveryForm: DeliveryForm;
    parentDrawbackPrevention: boolean;
    setWarningFlag?: (value: boolean) => void;
    setLicenseSelectionErrors?: (value: boolean) => void;
    selectedTier: string;
    deliveryMode: boolean;
}

export const isLicenseExpired = (expirationDate: string): boolean => {
    const expiration = new Date(formatDateWithoutTime(expirationDate));
    const presentDate = new Date();
    presentDate.setHours(HOUR, MINUTES_SECONDS_MILLISECONDS, MINUTES_SECONDS_MILLISECONDS);
    expiration.setHours(HOUR, MINUTES_SECONDS_MILLISECONDS, MINUTES_SECONDS_MILLISECONDS);
    const timeDifference = expiration.getTime() - presentDate.getTime();
    const daysDifference = Math.round(timeDifference / (1000 * 60 * 60 * 24));
    if (daysDifference < 0) {
        return true;
    }
    return false;
};

export const deduceRemainingParentBalance = (license: License, parentDrawbackPrevention: boolean): number => {
    if (REUSE_LICENSE_IDS.includes(license.licenseType)) {
        return license.parentAvailableAmount;
    }
    if (license.deliveryMode === DeliveryMode.SET) {
        return roundToOneDecimal(license.parentAvailableAmount + (license.available - license.licensesToAdd));
    }
    const licensesToAddCausesDrawback = license.licensesToAdd < 0 && parentDrawbackPrevention && license.available > 0;
    if (!licensesToAddCausesDrawback && license.licensesToAdd) {
        return roundToOneDecimal(license.parentAvailableAmount - license.licensesToAdd);
    }
    return license.parentAvailableAmount;
};

export const statusBadgeTitle = (t: TFunction, expiration: string) => {
    const status: Status = Status.ERROR;
    const expirationDate = formatDateWithoutTime(expiration);
    const translationKey = t("Licenses.expiredDate", { expirationDate: expirationDate });
    return {
        status: status,
        title: translationKey,
    };
};

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

const LicenseSelectionForm = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const selectedLicensesImmutable = props.ownedLicenses.length > 0 ? props.ownedLicenses : [];
    const [selectedSubscriptionLicenses, setSelectedSubscriptionLicenses] = React.useState<string[]>([]);
    const DEFAULT_SELECTED_LICENSE = createDefaultSelectedLicense(t);
    const [globalSettersVisibility, setGlobalSettersVisibility] = React.useState(false);
    const originalExpirationDates = React.useRef<Map<number, string>>(new Map());
    const previousLicenseTypes = React.useRef<Map<number, string>>(new Map());

    React.useEffect(() => {
        props.selectedLicenses.forEach((license) => {
            const previousLicenseType = previousLicenseTypes.current.get(license.index);
            const currentLicenseType = license.licenseType;
            if (
                !originalExpirationDates.current.has(license.index) ||
                (previousLicenseType && previousLicenseType !== currentLicenseType)
            ) {
                originalExpirationDates.current.set(license.index, license.expirationDate);
            }
            previousLicenseTypes.current.set(license.index, currentLicenseType);
        });
    }, [props.selectedLicenses]);

    const getOriginalExpirationDate = (license: License): string => {
        return originalExpirationDates.current.get(license.index) || license.expirationDate;
    };

    const areAllSelectedInAvailable = props.selectedLicenses.every((selected) =>
        props.availableLicenses.some((available) => available.licenseType === selected.licenseType)
    );
    const areAllAvailableInSelected = props.availableLicenses.every((available) =>
        props.selectedLicenses.some((selected) => available.licenseType === selected.licenseType)
    );

    const areAllParentLicensesAssigned = props.availableLicenses.every((each) =>
        each.licenseType == FEATURE_TENANT_MANAGEMENT
            ? each.licensesToAdd == 1
            : each.licensesToAdd == 0 &&
              props.ownedLicenses.some((available) => available.licenseType != each.licenseType)
    );

    const addRow = () => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE_DELIVERY,
            action: Action.ADD_LICENSE_TO_DELIVERY,
        });
        const newAdded: License = {
            licenseType: DEFAULT_SELECTED_LICENSE.licenseType,
            expirationDate: props.selectedLicenses[props.selectedLicenses.length - 1].expirationDate,
            productName: DEFAULT_SELECTED_LICENSE.productName,
            available: DEFAULT_SELECTED_LICENSE.available,
            licensesToAdd: DEFAULT_SELECTED_LICENSE.licensesToAdd,
            totalOfLicenses: DEFAULT_SELECTED_LICENSE.totalOfLicenses,
            index: Math.max(...props.selectedLicenses.map((item) => item.index)) + 1,
            parentAvailableAmount: DEFAULT_SELECTED_LICENSE.parentAvailableAmount,
            parentLicenseExpirationDate: DEFAULT_SELECTED_LICENSE.parentLicenseExpirationDate,
        };
        const licenses = props.selectedLicenses.concat([newAdded]);
        props.setSelectedLicenses(licenses);
    };

    const removeRow = (index: number) => {
        if (props.selectedLicenses.length === 1) {
            props.setSelectedLicenses([createDefaultSelectedLicense(t)]);
        } else {
            props.setSelectedLicenses(props.selectedLicenses.filter((each) => each.index !== index));
        }
    };
    const rebuildSubscriptionLicenses = () => {
        const licenseListBuilt = props.selectedLicenses
            .filter((e) => e.parentAvailableAmount == e.licensesToAdd && e.licensesToAdd > 0)
            .filter((e) => FEATURE_LICENSES.includes(e.licenseType) || isSubscriptionLicense(e.licenseType))
            .map((e) => e.productName);

        setSelectedSubscriptionLicenses(licenseListBuilt);
    };

    React.useEffect(() => {
        rebuildSubscriptionLicenses();
    }, [props.selectedLicenses]);

    React.useEffect(() => {
        const errorHandler = props.setLicenseSelectionErrors;
        if (errorHandler !== undefined) {
            const hasError = props.selectedLicenses.some(
                (license) =>
                    displayAmountErrorLabel(license, t) != null ||
                    !isExpirationDateValid(
                        license.expirationDate,
                        formatFormDate(
                            license.parentLicenseExpirationDate
                                ? license.parentLicenseExpirationDate
                                : generateLastDate()
                        )
                    )
            );

            errorHandler(hasError);
        }
    }, [props.selectedLicenses]);

    React.useEffect(() => {
        if (props.ownedLicenses.length > 0) {
            const sortedLicenses = props.ownedLicenses.sort((value1, value2) =>
                value1.productName.localeCompare(value2.productName)
            );
            props.setSelectedLicenses(sortedLicenses);
        }
    }, []);

    const isAddMoreButtonHidden = (): boolean => {
        return areAllAvailableInSelected || areAllParentLicensesAssigned;
    };

    const updateWarningFlag = () => {
        if (props.setWarningFlag !== undefined) {
            props?.setWarningFlag(true);
        }
    };

    function getDropDownLicensesList() {
        const selectedLicensesChanged = selectedLicensesImmutable !== props.selectedLicenses;
        const areSomeSelectedInAvailable = props.selectedLicenses.some((selected) =>
            props.availableLicenses.some((available) => available.licenseType === selected.licenseType)
        );
        if (props.availableLicenses.length < 0) {
            return props.selectedLicenses;
        } else if (areSomeSelectedInAvailable && selectedLicensesChanged) {
            return getDistinctList([...props.selectedLicenses, ...props.availableLicenses]);
        } else if (areAllSelectedInAvailable || areSomeSelectedInAvailable) {
            return props.availableLicenses;
        } else {
            return getDistinctList([...props.selectedLicenses, ...props.availableLicenses]);
        }
    }

    function getDistinctList(licenses: License[]) {
        return Array.from(new Map(licenses.map((item) => [item.licenseType, item])).values());
    }

    function getSelectedDateValue(license: License) {
        const formatted = formatFormDateWithoutTime(license.expirationDate);
        return parseDate(formatted);
    }

    function showGlobalSetters(): JSX.Element {
        return (
            <button
                className={classNames(style.link, buttons.textButton, style.addMoreButton)}
                onClick={() => setGlobalSettersVisibility(!globalSettersVisibility)}
                type="button"
                data-testid={
                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.globalSettersVisibilityButton
                }
            >
                {globalSettersVisibility
                    ? t("DeliveryHistory.addLicenseDelivery.bulkActionsHidden")
                    : t("DeliveryHistory.addLicenseDelivery.bulkActions")}
            </button>
        );
    }

    const handleOkClick = (date: Date, license: License) => {
        props.setSelectedLicenses(
            props.selectedLicenses.map((each) => {
                if (each.index === license.index) {
                    each.expirationDate = formatFormDate(date.toString());
                }
                return each;
            })
        );
    };

    const dropDownLicensesList: License[] = getDropDownLicensesList();

    return (
        <>
            <div className={classNames(form.formFields)}>
                <SubscriptionExpiryWarningBanner productNames={selectedSubscriptionLicenses} />
            </div>
            <div hidden={!globalSettersVisibility}>
                <GlobalSet
                    newDeal={props.newDeal}
                    showAmountType={false}
                    amount={props.deliveryForm.amount}
                    expirationDate={props.deliveryForm.expirationDate}
                    onAmountChange={(value) => {
                        props.deliveryForm["amount"] = parseInt(value);
                        props.setSelectedLicenses(
                            props.selectedLicenses.map((each) => ({
                                ...each,
                                licensesToAdd: parseInt(value),
                                totalOfLicenses: isNaN(parseInt(value))
                                    ? each.available
                                    : each.available + parseInt(value),
                            }))
                        );
                    }}
                    dateString={generateLastDate()}
                    onDateChange={(value) => {
                        props.deliveryForm["expirationDate"] = value;
                        props.setSelectedLicenses(
                            props.selectedLicenses.map((each) => ({
                                ...each,
                                expirationDate: value,
                            }))
                        );
                    }}
                    deliveryMode={props.deliveryMode ? (props.deliveryForm.deliveryMode as DeliveryMode) : ""}
                    onDeliveryModeChange={(value) => {
                        props.deliveryForm["deliveryMode"] = value as DeliveryMode;
                        props.setSelectedLicenses(
                            props.selectedLicenses.map((each) => ({
                                ...each,
                                deliveryMode: value as DeliveryMode,
                                licensesToAdd: value === DeliveryMode.SET ? each.available : 0,
                                totalOfLicenses: each.available,
                            }))
                        );
                    }}
                />
            </div>
            <Box>
                <StaticTable
                    headers={[
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.licenseType"),
                        },
                        ...(props.deliveryMode
                            ? [
                                  {
                                      value: t("DeliveryHistory.addLicenseDelivery.table.deliveryMode"),
                                  },
                              ]
                            : []),
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.available"),
                        },
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.licensesToAdd"),
                        },
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.totalOfLicenses"),
                        },
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.remainingLicenses"),
                        },
                        {
                            value: t("DeliveryHistory.addLicenseDelivery.table.expirationDate"),
                        },
                    ]}
                    cells={props.selectedLicenses.map((license) => {
                        return [
                            <Tooltip key={license.licenseType} content={license.productName}>
                                <div className={style.textAndBannerContainer}>
                                    <select
                                        className={classNames(
                                            form.select,
                                            style.fixedWidthInput,
                                            style.ellipsis,
                                            style.selectArrow
                                        )}
                                        id={"licenseType" + license.index}
                                        name={"licenseType" + license.index}
                                        key={"licenseType" + license.index}
                                        onChange={(e) => {
                                            const selected = getDropDownLicensesList().find(
                                                (item) => item.licenseType === e.target.value
                                            );
                                            if (selected !== undefined) {
                                                props.setSelectedLicenses(
                                                    props.selectedLicenses.map((each) => {
                                                        if (license.index === each.index) {
                                                            each = selected;
                                                            each.index = license.index;
                                                            const selectedLicensesLength =
                                                                props.selectedLicenses.length;
                                                            if (
                                                                selectedLicensesLength >= 1 &&
                                                                each.available == 0 &&
                                                                license.licenseType === "default"
                                                            ) {
                                                                each.assigned = 0;
                                                            }
                                                            if (
                                                                each.licenseType ===
                                                                MOBILE_DIAGNOSTICS_ERASURE_LICENSE_REUSE_ID_AMOUNT
                                                            ) {
                                                                if (props.selectedTier === "MOBILE_PROCESSOR_PRO") {
                                                                    each.licensesToAdd = LICENSE_REUSE_PRO_AMOUNT;
                                                                    each.totalOfLicenses =
                                                                        each.available + LICENSE_REUSE_PRO_AMOUNT;
                                                                } else if (
                                                                    props.selectedTier === "MOBILE_PROCESSOR_PRO_SCALE"
                                                                ) {
                                                                    each.licensesToAdd = LICENSE_REUSE_PRO_SCALE_AMOUNT;
                                                                    each.totalOfLicenses =
                                                                        each.available + LICENSE_REUSE_PRO_SCALE_AMOUNT;
                                                                }
                                                            } else {
                                                                if (props.deliveryForm.amount != null) {
                                                                    each.licensesToAdd = props.deliveryForm.amount;
                                                                    each.totalOfLicenses = isNaN(
                                                                        props.deliveryForm.amount
                                                                    )
                                                                        ? each.available
                                                                        : each.available + props.deliveryForm.amount;
                                                                    each.assigned = 0;
                                                                }
                                                            }

                                                            if (props.deliveryForm.expirationDate != "") {
                                                                each.expirationDate = props.deliveryForm.expirationDate;
                                                            }
                                                        }
                                                        return each;
                                                    })
                                                );
                                            }
                                        }}
                                        onClick={() => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.LICENSE_DELIVERY,
                                                action: Action.CHANGE_LICENSE_TYPE,
                                            });
                                        }}
                                        value={license.licenseType}
                                        data-testid={
                                            testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                .licenseTypeSelect.itself
                                        }
                                    >
                                        <option value={"default"}>
                                            {t("DeliveryHistory.addLicenseDelivery.table.default")}
                                        </option>
                                        {dropDownLicensesList
                                            .filter((each) => each.licenseType != "default")
                                            .map((each) => (
                                                <option
                                                    key={each.licenseType}
                                                    value={each.licenseType}
                                                    defaultValue={license.licenseType}
                                                    hidden={
                                                        each.licenseType !== license.licenseType &&
                                                        props.selectedLicenses.some(
                                                            (item) => item.licenseType === each.licenseType
                                                        )
                                                    }
                                                >
                                                    {each.productName}
                                                </option>
                                            ))}
                                    </select>
                                    {isLicenseExpired(getOriginalExpirationDate(license)) && (
                                        <div>
                                            <span className={classNames(style.statusBadge)}>
                                                <StatusBadge
                                                    values={[statusBadgeTitle(t, getOriginalExpirationDate(license))]}
                                                    tooltip={true}
                                                />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </Tooltip>,
                            ...(props.deliveryMode
                                ? [
                                      <Tooltip
                                          key={license.licenseType}
                                          content={t("DeliveryHistory.addLicenseDelivery.table.hoverMessage")}
                                      >
                                          <div key={"deliveryMode" + license.index}>
                                              <select
                                                  className={classNames(form.select, style.assignmentWidthInput)}
                                                  value={license.deliveryMode || DeliveryMode.ADD}
                                                  onChange={(e) => {
                                                      const newMode = e.target.value as DeliveryMode;
                                                      props.setSelectedLicenses(
                                                          props.selectedLicenses.map((each) => {
                                                              if (each.index === license.index) {
                                                                  each.deliveryMode = newMode;
                                                                  each.licensesToAdd =
                                                                      newMode === DeliveryMode.SET ? each.available : 0;
                                                                  each.totalOfLicenses = each.available;
                                                              }
                                                              return each;
                                                          })
                                                      );
                                                  }}
                                                  data-testid={
                                                      testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                          .deliveryModeSelect.itself
                                                  }
                                              >
                                                  <option value={DeliveryMode.ADD}>
                                                      {t("DeliveryHistory.addLicenseDelivery.table.add")}
                                                  </option>
                                                  <option value={DeliveryMode.SET}>
                                                      {t("DeliveryHistory.addLicenseDelivery.table.set")}
                                                  </option>
                                              </select>
                                          </div>
                                      </Tooltip>,
                                  ]
                                : []),
                            <div
                                key={"available" + license.index}
                                className={style.label}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.availableLabel
                                }
                            >
                                {(!isUserParentInternal() || hasTenantCookie()) &&
                                (isSubscriptionLicense(license.licenseType) ||
                                    FEATURE_LICENSES.includes(license.licenseType))
                                    ? t("DeliveryHistory.addLicenseDelivery.table.subscription", {
                                          totalSubscriptionLicenses: license.available,
                                      })
                                    : license.available}
                            </div>,
                            <div key={"assigned" + license.index}>
                                <input
                                    autoFocus
                                    className={classNames(form.input, style.fixedWidth)}
                                    key={"added" + license.index}
                                    id={"added" + license.index}
                                    type={"number"}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        const parsedValue = inputValue === "" ? 0 : parseInt(inputValue) || 0;
                                        props.setSelectedLicenses(
                                            props.selectedLicenses.map((each) => {
                                                if (each.index === license.index) {
                                                    each.licensesToAdd = parsedValue;
                                                    if (each.deliveryMode === DeliveryMode.SET) {
                                                        each.totalOfLicenses = parsedValue;
                                                    } else {
                                                        each.totalOfLicenses = isNaN(each.licensesToAdd)
                                                            ? each.available
                                                            : each.available + each.licensesToAdd;
                                                    }
                                                }
                                                return each;
                                            })
                                        );
                                    }}
                                    value={license.licensesToAdd.toString()}
                                    data-testid={
                                        testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                            .assignedAmountInput.itself
                                    }
                                />
                                {license.licensesToAdd < 0 &&
                                    props.parentDrawbackPrevention &&
                                    license.available > 0 &&
                                    (updateWarningFlag(),
                                    (
                                        <Tooltip
                                            content={t("DeliveryHistory.addLicenseDelivery.negativeAmountWarning")}
                                        >
                                            <div className={style.warningIconWidth}>
                                                <Warning
                                                    color={props.theme.secondaryWarningBackgroundColor}
                                                    width={20}
                                                    height={20}
                                                />
                                            </div>
                                        </Tooltip>
                                    ))}
                                {displayAmountErrorLabel(license, t)}
                            </div>,
                            <div
                                key={"total" + license.index}
                                className={style.totalLabel}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.totalOfLicensesLabel
                                        .itself
                                }
                            >
                                {license.deliveryMode === DeliveryMode.SET
                                    ? license.licensesToAdd
                                    : license.totalOfLicenses}
                                {license.totalOfLicenses < 0 ? (
                                    <div
                                        className={classNames(form.error, style.totalLabelError)}
                                        data-testid={
                                            testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                .totalOfLicensesLabel.errorLabel
                                        }
                                    >
                                        <span>
                                            {t("DeliveryHistory.addLicenseDelivery.validation.totalOfLicenses")}
                                        </span>
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>,
                            <div
                                key={"remaining" + license.index}
                                className={classNames(style.remainingBalanceCell, style.label)}
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.createDeliveryDialog.availableLabel
                                }
                            >
                                {deduceRemainingParentBalance(license, props.parentDrawbackPrevention)}
                                {REUSE_LICENSE_IDS.includes(license.licenseType) && (
                                    <BccTooltip>
                                        <TooltipTrigger asChild>
                                            <span className={style.infoIcon}>
                                                <Info size={16} color="--theme-icon-fill-default" />
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {t("DeliveryHistory.addLicenseDelivery.table.reuseLicenseTooltip")}
                                        </TooltipContent>
                                    </BccTooltip>
                                )}
                            </div>,
                            <div key={"expirationDate" + license.index} className={style.gridColumns}>
                                <div>
                                    <DatePicker
                                        format={DATE_FORMAT}
                                        id={"expirationInput" + license.index}
                                        caretAs={(colorProps) => (
                                            <CalendarIcon {...colorProps} color={props.theme.iconFillColor} />
                                        )}
                                        className={classNames(form.dateRange, {
                                            [form.inputError]: !isExpirationDateValid(
                                                license.expirationDate,
                                                formatFormDate(
                                                    license.parentLicenseExpirationDate
                                                        ? license.parentLicenseExpirationDate
                                                        : generateLastDate()
                                                )
                                            ),
                                        })}
                                        placement={"autoVerticalEnd"}
                                        cleanable={false}
                                        disabledDate={(date: Date) => {
                                            const normalizedDate = new Date(
                                                date.getFullYear(),
                                                date.getMonth(),
                                                date.getDate()
                                            );
                                            const lastDate = formatFormDate(
                                                license.parentLicenseExpirationDate
                                                    ? license.parentLicenseExpirationDate
                                                    : generateLastDate()
                                            );
                                            return normalizedDate > moment(lastDate).toDate();
                                        }}
                                        value={getSelectedDateValue(license)}
                                        data-testid={
                                            testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                .expirationDateInput.itself
                                        }
                                        editable={true}
                                        onChange={(date: Date) => {
                                            if (date && !isNaN(date.getTime())) {
                                                const year = date.getFullYear();
                                                if (year < 1900 || year > 2100) {
                                                    return;
                                                }
                                                const parsedDate = moment(date).toDate();
                                                handleOkClick(parsedDate, license);
                                            }
                                        }}
                                    />
                                    {!isExpirationDateValid(
                                        license.expirationDate,
                                        formatFormDate(
                                            license.parentLicenseExpirationDate
                                                ? license.parentLicenseExpirationDate
                                                : generateLastDate()
                                        )
                                    ) && (
                                        <div
                                            className={classNames(form.error, style.datePickerError)}
                                            data-testid={
                                                testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                    .expirationDateInput.errorLabel
                                            }
                                        >
                                            <span className={style.wrapped}>
                                                {t("DeliveryHistory.addLicenseDelivery.validation.expirationDate", {
                                                    minDate: MIN_DATE.toLocaleDateString(),
                                                    maxDate: formatFormDateWithoutTime(
                                                        license.parentLicenseExpirationDate
                                                            ? license.parentLicenseExpirationDate
                                                            : generateLastDate()
                                                    ),
                                                })}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div key={"deleteIcon" + license.index} className={style.gridRows}>
                                    {props.selectedLicenses.length >= 1 ? (
                                        <div
                                            key={"deleteRow" + license.index}
                                            className={classNames(style.margin, buttons.textButton)}
                                            onClick={() => {
                                                usageStatisticsService.sendEvent({
                                                    category: Category.LICENSE_DELIVERY,
                                                    action: Action.REMOVE_LICENSE,
                                                    label: license.licenseType,
                                                });
                                                removeRow(license.index);
                                            }}
                                            data-testid={
                                                testIds.workArea.license.deliveryHistory.createDeliveryDialog
                                                    .removeRowButton
                                            }
                                        >
                                            <DeleteIcon
                                                color={props.theme.contentBackgroundColor}
                                                linecolor={props.theme.iconFillColor}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>,
                        ];
                    })}
                />
            </Box>
            <button
                onClick={addRow}
                className={classNames(style.link, buttons.textButton, style.addMoreButton)}
                data-testid={testIds.workArea.license.deliveryHistory.createDeliveryDialog.addLicenseButton}
                hidden={isAddMoreButtonHidden()}
                type="button"
            >
                {addMoreLicenses(
                    props.theme.contentBackgroundColor,
                    props.theme.iconFillColor,
                    t("Common.addMoreLicenses")
                )}
            </button>
            {showGlobalSetters()}
        </>
    );
};

export default connector(LicenseSelectionForm);
